/**
 * Dimensões disponíveis no sheet de relatórios.
 *
 * Cliente e Produto são lidos separadamente das cinco fontes heterogêneas para
 * que o mesmo id sobreviva à troca de aba. As duas leituras paginam: uma opção
 * fora das primeiras 1.000 linhas não pode simplesmente desaparecer do filtro.
 */
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { supabase } from '@/integrations/supabase/client';
import { fetchAllPages } from '@/lib/reportPaging';
import { getCachedCatalog, getCachedClients } from '@/lib/offlineDataCache';

export interface ReportClientOption {
  id: string;
  name: string;
}

export interface ReportProductOption {
  id: string;
  name: string;
  type: string | null;
  variantName: string | null;
  parentProductId: string | null;
  trackStock: boolean;
}

interface ReportDimensionsResult {
  clients: ReportClientOption[];
  products: ReportProductOption[];
  truncated: boolean;
}

function companyRows(rows: readonly any[], companyId: string | null): any[] {
  if (!companyId) return [];
  return rows.filter((row) => !row?.company_id || row.company_id === companyId);
}

function normalizeClients(rows: readonly any[]): ReportClientOption[] {
  return rows
    .filter((row) => row?.id)
    .map((row) => ({
      id: String(row.id),
      name: String(row.name ?? '').trim() || 'Cliente sem nome',
    }))
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }));
}

function normalizeProducts(rows: readonly any[]): ReportProductOption[] {
  const parentIds = new Set(
    rows.map((row) => row?.parent_product_id).filter(Boolean).map(String),
  );
  return rows
    // Pai de variações não é vendável/estocável; a opção correta é cada filha.
    .filter((row) => row?.id && !parentIds.has(String(row.id)))
    .map((row) => ({
      id: String(row.id),
      name: String(row.name ?? '').trim() || 'Item sem nome',
      type: row.type ? String(row.type) : null,
      variantName: row.variant_name ? String(row.variant_name) : null,
      parentProductId: row.parent_product_id ? String(row.parent_product_id) : null,
      trackStock: row.track_stock === true,
    }))
    .sort((a, b) => {
      const name = a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' });
      return name || String(a.variantName ?? '').localeCompare(String(b.variantName ?? ''), 'pt-BR');
    });
}

export function reportProductLabel(product: ReportProductOption): string {
  return product.variantName ? `${product.name} › ${product.variantName}` : product.name;
}

export function useReportDimensions() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;

  const query = useQuery<ReportDimensionsResult>({
    queryKey: ['report-dimensions', companyId],
    enabled: Boolean(user?.id && companyId),
    networkMode: 'offlineFirst',
    staleTime: 60 * 1000,
    refetchOnMount: 'always',
    // A query nasce DESABILITADA (sessão/empresa resolvem depois da montagem), então
    // `refetchOnMount` já passou quando ela habilita: sem o carimbo, o cache semeado
    // conta como FRESCO e a primeira busca nunca sai (#158).
    initialDataUpdatedAt: 0,
    initialData: () => {
      if (!user?.id || !companyId) return undefined;
      return {
        clients: normalizeClients(companyRows(getCachedClients(user.id) ?? [], companyId)),
        products: normalizeProducts(companyRows(getCachedCatalog(user.id) ?? [], companyId)),
        truncated: false,
      };
    },
    queryFn: async () => {
      if (!companyId) return { clients: [], products: [], truncated: false };

      const [clients, products] = await Promise.all([
        fetchAllPages<any>((start, end) =>
          supabase
            .from('clients')
            .select('id, name, company_id')
            .eq('company_id', companyId)
            .order('name', { ascending: true })
            .order('id', { ascending: true })
            .range(start, end) as any,
        ),
        fetchAllPages<any>((start, end) =>
          supabase
            .from('products')
            .select('id, name, type, variant_name, parent_product_id, track_stock, company_id')
            .eq('company_id', companyId)
            .order('name', { ascending: true })
            .order('id', { ascending: true })
            .range(start, end) as any,
        ),
      ]);

      return {
        clients: normalizeClients(clients.rows),
        products: normalizeProducts(products.rows),
        truncated: clients.truncated || products.truncated,
      };
    },
  });

  return {
    clients: query.data?.clients ?? [],
    products: query.data?.products ?? [],
    truncated: query.data?.truncated ?? false,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
